import { useState } from 'react'

import {
  useAllSamples,
  useCorrectSample,
  useRegisterSample,
  useSample,
  useSampleReport,
} from '@ctcells/api'
import { PATHS, dataSourceFor } from '@ctcells/core'
import { SAMPLES } from '@ctcells/fixtures'
import { errorMessage, t } from '@ctcells/i18n'
import { useLocation, useNavigate, useParams } from 'react-router'

import { gate, type ActionGate } from '../access.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { SampleDetailScreen } from './SampleDetailScreen.tsx'
import { registerFailureOf, registerSampleVars } from './sampleModel.ts'
import { SampleNewScreen } from './SampleNewScreen.tsx'
import type { ResourceTabId } from '../resources/resourceTabs.ts'

import { nextTrialSampleId, trialSampleVars } from './sampleReregister.ts'
import { SamplesScreen } from './SamplesScreen.tsx'

/**
 * 시료 — 목록 · 상세(정정) · 등록 컨테이너.
 *
 * 분류: **1. 실제로 연결** — `GET /samples`(실물 50건) · `GET /samples/{id}` ·
 * `PUT /samples/{id}` · `POST /samples`(2026-09-17 실측).
 *
 * 🔴 **화면은 0줄도 안 바뀐다.** 훅을 부르고 props 로 내려줄 뿐이다.
 * 🔴 **갈리는 층은 `dataSourceFor('samples')` 하나다.** demo 경로는 네트워크 훅을 부르지 않는다.
 *
 * ⚠ **깨끗하지 않은 지점 — 목록 끝을 알 수 없다.**
 *   `GET /samples` 는 `limit`·`offset` 을 **받으면서 `total_count` 를 주지 않는다**(벌거벗은 배열).
 *   그래서 「마지막 페이지인가」는 `items.length < limit` 추론이고(`hasMore()`), 정확히 `limit` 개인
 *   마지막 페이지에서 빈 응답을 한 번 더 긁는다. → 서버 `total_count` 가 정답이다(보고 대상 J).
 *   지금은 **페이지 이동 자체를 쓰지 않는다** — 한 번에 상한까지 받고 끝낸다. 정렬 파라미터도
 *   없어서(실측) 2페이지의 의미가 서버 기본 정렬에 좌우되기 때문이다.
 *
 * ⚠ **검색·상태 필터를 서버로 못 넘긴다.** `/samples` 의 질의 파라미터가 `limit`·`offset` 뿐이다.
 *   화면의 검색창·탭은 **받아 온 목록 안에서만** 거른다. 상한 밖의 시료는 검색에 걸리지 않는다.
 *
 * ⚠ **정정 2026-09-26 — 종전 「임상 6항목이 실물에 오지 않는다」는 낡았다.** 등록 때 실은 임상
 *   값은 돌아온다(`@ctcells/contract` `Sample` 머리말의 실측 표). 지금 0건인 것은 자리 셋
 *   (`current_position`·`assigned_well`·`sample_ref`)이고 화면은 `—` 로 그린다 — **지어내지 않는다.**
 *
 * 경계 다섯
 * | 로딩 | 「불러오는 중」. 화면을 그리지 않는다(목 fixture 가 실물인 척하지 않게) |
 * | 에러 | `errorMessage()` — 에러 코드·규칙 ID 를 노출하지 않는다               |
 * | 빈   | 0행이면 화면의 `emptyLabel` 이 「시료 없음」을 그린다                  |
 * | 권한 | `useScreenAccess()` → 숨기지 않고 비활성 + 사유                        |
 * | 끊김 | 같은 `access.controlDisabled`. `gate()` 가 1순위로 막는다              |
 */

/**
 * 한 번에 받는 상한. 서버 최대가 500 이고 정렬 파라미터가 없어 페이지 이동이 무의미하다
 * (2페이지의 내용이 서버 기본 정렬에 좌우된다). 🔴 숫자를 화면에 박지 않는다 — 여기 한 곳이다.
 */

/**
 * @param resourceTab 열린 무리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다.
 *   **안 넘기면 무리 탭 줄을 안 그린다**(`useGroupTabLayout` 주석). 장비 관점 콘솔이 그렇게 쓴다.
 */
export interface SamplesContainerProps {
  resourceTab?: ResourceTabId | undefined
  /** 오른쪽 판에 열린 시료 — 그 행을 «고른 행»으로 그린다 */
  selectedSampleId?: string | undefined
}

export function SamplesContainer(props: SamplesContainerProps = {}) {
  return dataSourceFor('samples') === 'demo' ? (
    <SamplesDemo {...props} />
  ) : (
    <SamplesLive {...props} />
  )
}

/** demo 경로는 props 를 안 넘긴다 = 화면 기본값(`SAMPLES`)이 그대로 산다 */
function SamplesDemo({ resourceTab, selectedSampleId }: SamplesContainerProps) {
  const access = useScreenAccess()
  return (
    <SamplesScreen
      access={access}
      selectedSampleId={selectedSampleId}
      {...(resourceTab === undefined ? {} : { resourceTab })}
    />
  )
}

function SamplesLive({ resourceTab, selectedSampleId }: SamplesContainerProps) {
  const access = useScreenAccess()
  // 🔴 전부 받는다 — 목록은 서버 순(sample_id)이라 한 쪽만 받으면 최근 시료가 빠진다(`useAllSamples` 머리말)
  const samples = useAllSamples({})

  /*
   * 🔴 **화면째 에러로 바꾸지 않는다**(2026-09-21). `GET /samples` 는 viewer 에게 403 이고
   *    (`views/samples.py` `READ_ROLES` · PII 접촉면) 「준비」 문이 **이 화면으로** 들어온다
   *    (`paths.ts` 의 `resources` 항목 `path: PATHS.samples`).
   *    종전처럼 `<ScreenState>` 한 줄로 갈아 끼우면 **준비 탭 줄이 같이 사라져** viewer 가
   *    레시피·랩웨어로 갈 길을 잃는다 — 서버는 그 둘에 200 을 준다.
   * ⇒ 틀은 남기고 표 안에만 사유를 적는다. 시료 탭 자체는 `resourceTabs.ts` 에서
   *   **비활성 + 사유**로 서고, 집행은 서버 403 이다.
   */
  const notice = samples.isError ? errorMessage(samples.error) : undefined

  return (
    <SamplesScreen
      {...(resourceTab === undefined ? {} : { resourceTab })}
      access={access}
      loading={samples.isPending}
      notice={notice}
      samples={samples.data?.items ?? []}
      selectedSampleId={selectedSampleId}
    />
  )
}

export interface SampleDetailContainerProps {
  /** 서랍(목록 오른쪽 판)으로 열렸을 때의 닫기. 없으면 전체 화면이고 「← 시료」 줄이다 */
  onClose?: (() => void) | undefined
}

export function SampleDetailContainer(props: SampleDetailContainerProps = {}) {
  return dataSourceFor('samples') === 'demo' ? (
    <SampleDetailDemo {...props} />
  ) : (
    <SampleDetailLive {...props} />
  )
}

function SampleDetailDemo({ onClose }: SampleDetailContainerProps) {
  const access = useScreenAccess()
  return (
    <SampleDetailScreen
      access={access}
      /* demo 는 POST 를 보내지 않는다 — 누를 수 있는데 아무 일도 없는 단추를 두지 않는다 */
      reregisterGate={{ disabled: true, disabledReason: t('reason.demoSource') }}
      onReregister={() => undefined}
      onClose={onClose}
      hideReport={onClose !== undefined}
      preparationDrawer={onClose !== undefined}
    />
  )
}

/** 다시 등록 성공 뒤 새 서랍으로 넘기는 주소 상태 — 그 서랍에서만 알림이 선다 */
interface ReregisteredState {
  reregistered?: { from: string; to: string }
}

/**
 * 상세는 **한 건만** 받아 배열로 넘긴다. 화면이 `useParams()` 로 스스로 고르는 구조라
 * 「그 한 건만 담긴 목록」이 곧 상세다(화면 무수정).
 *
 * 🔴 정정은 **사유 필수**이고 되돌릴 수 없다. `useCorrectSample()` 이 성공 시 **무효화만** 한다 —
 *    화면을 미리 고치는 낙관적 전이가 없다(DP-04).
 * ✅ **입력칸이 이어졌다**(2026-09-21). 화면이 «바뀐 칸만» `patch` 로 만들어 넘기고
 *    여기서 그대로 본문에 실린다(`useCorrectSample` 이 `{command_id, reason, ...patch}`).
 *    종전에는 여섯 칸이 `onChange={() => undefined}` 라 나가는 것이 `reason` 하나였다.
 */
function SampleDetailLive({ onClose }: SampleDetailContainerProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const access = useScreenAccess()
  const { sampleId } = useParams()
  const sample = useSample({ sampleId })
  /*
   * 정정 폼의 종류 · 암종 · 병기 제안(2026-10-01 회의). 🔴 **목록과 같은 쿼리 키**(`scope: 'all'`)라 서랍에서는
   * 왼쪽 목록이 이미 받은 캐시를 읽는다 — 요청이 하나 더 나가지 않는다. 못 받으면(403 · 실패) 제안만 비고 입력은 산다.
   */
  const known = useAllSamples({})
  const correct = useCorrectSample()
  /*
   * 「시험용으로 다시 등록」 — 🔴 **창 없이 바로** `POST /samples` 한 번(eric 2026-10-29 「같은 스펙으로 바로 만들어 버려」).
   * 원본의 등록 칸을 그대로 싣고 번호는 원본 + 짧은 uuid 8자다(`sampleReregister.ts`). 번호가 이미 있으면(409) 새로 뽑아 한 번 더.
   * 성공하면 목록은 `useRegisterSample` 의 무효화로 다시 받고, 새 시료의 서랍을 연다. 실패면 이 서랍 머리에 서버 문구 · 이동 없음.
   */
  const register = useRegisterSample()
  const [reregisterError, setReregisterError] = useState<{
    sampleId: string
    message: string
  } | null>(null)
  const reregister = async (source: NonNullable<typeof sample.data>) => {
    setReregisterError(null)
    const existing = known.data?.items ?? []
    let lastError: unknown
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const newId = nextTrialSampleId(source.sample_id, existing)
      try {
        await register.mutateAsync(trialSampleVars(source, newId))
        const state: ReregisteredState = { reregistered: { from: source.sample_id, to: newId } }
        void navigate(`${PATHS.samples}/${encodeURIComponent(newId)}`, { state })
        return
      } catch (error) {
        lastError = error
        if (!registerFailureOf(error).duplicate) break
      }
    }
    setReregisterError({
      sampleId: source.sample_id,
      message: registerFailureOf(lastError).message,
    })
  }
  const reregisterGate: ActionGate = register.isPending
    ? { disabled: true, disabledReason: t('reason.actionInFlight') }
    : gate(access, { perm: 'control' })
  const reregistered = (location.state as ReregisteredState | null)?.reregistered
  /*
   * 「보고서」 — `GET /reports/{sample_id}`(조회뿐 · 2026-09-28 이었다). 누를 때만 쏜다.
   * 🔴 **연 시료가 지금 서랍의 시료일 때만** 대화를 넘긴다 — 다른 시료로 옮기면 저절로 닫힌다.
   * ⚠ viewer 는 버튼이 비활성이라 쏘지 않는다(`view` 없음). 쏘더라도 403 은 **대화 안에만** 선다.
   */
  const [reportSampleId, setReportSampleId] = useState<string | null>(null)
  const reportOpen = reportSampleId !== null && reportSampleId === sampleId
  const report = useSampleReport({ sampleId: reportOpen ? reportSampleId : undefined })

  if (sample.isPending) return <ScreenLoading />
  // 🔴 404 도 에러다. 「없는 시료」를 빈 화면으로 얼버무리지 않는다
  if (sample.isError) return <ScreenState text={errorMessage(sample.error)} tone="error" />

  const loaded = sample.data
  return (
    <SampleDetailScreen
      access={access}
      reregisterGate={reregisterGate}
      onReregister={() => void reregister(loaded)}
      reregisterError={
        reregisterError !== null && reregisterError.sampleId === sampleId
          ? reregisterError.message
          : undefined
      }
      notice={
        reregistered !== undefined && reregistered.to === sampleId
          ? t('sampleNew.reregister.done', { id: reregistered.to })
          : undefined
      }
      onClose={onClose}
      hideReport={onClose !== undefined}
      preparationDrawer={onClose !== undefined}
      saveError={
        correct.isError && correct.variables?.sampleId === sampleId
          ? errorMessage(correct.error)
          : undefined
      }
      samples={[sample.data]}
      hintSamples={known.data?.items ?? []}
      onSave={(id, patch, reason) => {
        correct.mutate({ sampleId: id, reason, patch })
      }}
      onOpenReport={setReportSampleId}
      {...(reportOpen
        ? {
            reportPanel: {
              sampleId: reportSampleId,
              report: report.data,
              loading: report.isPending,
              error: report.error === null ? undefined : errorMessage(report.error),
              onClose: () => setReportSampleId(null),
            },
          }
        : {})}
    />
  )
}

export function SampleNewContainer() {
  return dataSourceFor('samples') === 'demo' ? <SampleNewDemo /> : <SampleNewLive />
}

/** demo 는 fixture 가 «이미 등록된 시료»다 — 이어지는 번호 · 제안이 그것으로 선다 */
function SampleNewDemo() {
  const access = useScreenAccess()
  return <SampleNewScreen access={access} existing={SAMPLES} />
}

/**
 * 등록. 🔴 **필수는 `sample_id` 뿐**이고 임상 3항목은 배치 종료 후 소급 수집이 불가능해
 * 등록 화면이 항상 노출한다(계약 산문). 비면 보내지 않는다 — 빈 문자열을 값으로 만들지 않는다.
 */
function SampleNewLive() {
  const access = useScreenAccess()
  const register = useRegisterSample()
  /*
   * 「이미 있는 번호」 경고 · 제안의 재료 — 🔴 **부가 조회다.** 403(viewer) · 실패여도 등록 화면은 선다.
   * 사유는 시료 목록 절 그 자리에만 적는다(「못 봤다」를 「0건」으로 접지 않는다).
   */
  const existing = useAllSamples({})

  return (
    <SampleNewScreen
      access={access}
      existing={existing.data?.items}
      existingError={existing.isError ? errorMessage(existing.error) : undefined}
      existingLoading={existing.isPending}
      onRegister={async (draft, commandId) => {
        try {
          await register.mutateAsync({ ...registerSampleVars(draft), commandId })
          return { ok: true }
        } catch (error) {
          const outcome = registerFailureOf(error)
          // 중복이면 목록이 낡은 것이다 — 다시 받아 다음 번호 제안을 바로잡는다
          if (outcome.duplicate) void existing.refetch()
          return outcome
        }
      }}
    />
  )
}
